import * as React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNotification } from "../../context/notification-provider";
import { useDialogs } from "../dialog/use-dialog";
import useClients from "../client-config/data/use-clients";
import useFilterTypes from "./use-filter-types";
import {
  disableFilterForClient,
  enableFilterForClient,
  getClientOverrides,
  resetClientToDefault,
} from "./service";
import { getColumns } from "./client-visibility-data-grid";
import { ClientVisibilityRow } from "./client-visibility-types";
import { FilterDefaultVisibility, FilterTypeDetail } from "./types";

const buildRows = (
  filterTypes: FilterTypeDetail[],
  overrides: { filterTypeId: number; isEnabled: boolean }[],
): ClientVisibilityRow[] =>
  filterTypes.map((filterType) => {
    const override = overrides.find(
      (item) => item.filterTypeId === filterType.id,
    );

    return {
      filterTypeId: filterType.id,
      code: filterType.code,
      label: filterType.label,
      defaultVisibility: filterType.defaultVisibility,
      isOverridden: !!override,
      isEnabled: override
        ? override.isEnabled
        : filterType.defaultVisibility === FilterDefaultVisibility.VisibleToAll,
    };
  });

const ClientVisibility = () => {
  const { showNotification } = useNotification();
  const { confirm } = useDialogs();

  const {
    data: clients,
    error: loadingClientsError,
    isLoading: isLoadingClients,
  } = useClients();
  const {
    data: filterTypes,
    error: loadingFilterTypesError,
    isLoading: isLoadingFilterTypes,
  } = useFilterTypes();

  const [selectedClientId, setSelectedClientId] = React.useState<number | "">(
    "",
  );
  const [rows, setRows] = React.useState<ClientVisibilityRow[]>([]);
  const [isLoadingOverrides, setIsLoadingOverrides] = React.useState(false);
  const [overridesError, setOverridesError] = React.useState<unknown>(null);

  const activeClients = React.useMemo(
    () => clients?.filter((client) => !client.deleted) || [],
    [clients],
  );

  const loadOverridesForClient = React.useCallback(
    async (clientId: number) => {
      if (!filterTypes) return;

      setIsLoadingOverrides(true);
      try {
        const overrides = await getClientOverrides(clientId);
        setRows(buildRows(filterTypes, overrides));
        setOverridesError(null);
      } catch (err) {
        setOverridesError(err);
      } finally {
        setIsLoadingOverrides(false);
      }
    },
    [filterTypes],
  );

  React.useEffect(() => {
    if (selectedClientId === "" && activeClients.length > 0 && filterTypes) {
      const firstClientId = activeClients[0].id;
      setSelectedClientId(firstClientId);
      loadOverridesForClient(firstClientId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClients, filterTypes]);

  const handleClientChange = React.useCallback(
    (event: SelectChangeEvent<number>) => {
      const clientId = event.target.value as number;
      setSelectedClientId(clientId);
      loadOverridesForClient(clientId);
    },
    [loadOverridesForClient],
  );

  const handleRefreshClick = React.useCallback(() => {
    if (selectedClientId === "") return;
    showNotification({
      message: "Refreshing client visibility...",
      severity: "info",
      duration: 2000,
    });
    loadOverridesForClient(selectedClientId);
  }, [selectedClientId, showNotification, loadOverridesForClient]);

  const handleToggleEnabled = React.useCallback(
    async (row: ClientVisibilityRow) => {
      if (selectedClientId === "") return;
      const newValue = !row.isEnabled;
      const action = newValue ? "enable" : "disable";
      const clientName =
        activeClients.find((client) => client.id === selectedClientId)?.name ??
        "this client";

      try {
        const result = await confirm(
          `Are you sure you want to ${action} the filter "${row.label}" for "${clientName}"?`,
          {
            title: "Confirm Filter Visibility Change",
            okText: "Confirm",
            cancelText: "Cancel",
            severity: "warning",
          },
        );

        if (result === true) {
          if (newValue) {
            await enableFilterForClient(row.filterTypeId, selectedClientId);
          } else {
            await disableFilterForClient(row.filterTypeId, selectedClientId);
          }
          showNotification({
            message: `Filter "${row.label}" ${action}d for "${clientName}".`,
            severity: "success",
            duration: 3000,
          });
          loadOverridesForClient(selectedClientId);
        }
      } catch (toggleError) {
        showNotification({
          message: `Failed to update filter visibility. ${(toggleError as Error).message}`,
          severity: "error",
          duration: 3000,
        });
      }
    },
    [
      selectedClientId,
      activeClients,
      confirm,
      showNotification,
      loadOverridesForClient,
    ],
  );

  const handleReset = React.useCallback(
    async (row: ClientVisibilityRow) => {
      if (selectedClientId === "") return;
      const clientName =
        activeClients.find((client) => client.id === selectedClientId)?.name ??
        "this client";

      try {
        const result = await confirm(
          `Reset "${row.label}" to its default visibility for "${clientName}"?`,
          {
            title: "Confirm Reset to Default",
            okText: "Reset",
            cancelText: "Cancel",
            severity: "warning",
          },
        );

        if (result === true) {
          await resetClientToDefault(row.filterTypeId, selectedClientId);
          showNotification({
            message: `Filter "${row.label}" reset to default for "${clientName}".`,
            severity: "success",
            duration: 3000,
          });
          loadOverridesForClient(selectedClientId);
        }
      } catch (resetError) {
        showNotification({
          message: `Failed to reset filter visibility. ${(resetError as Error).message}`,
          severity: "error",
          duration: 3000,
        });
      }
    },
    [
      selectedClientId,
      activeClients,
      confirm,
      showNotification,
      loadOverridesForClient,
    ],
  );

  if (loadingClientsError || loadingFilterTypesError) {
    return (
      <Alert severity="error">Unable to load clients or filter types.</Alert>
    );
  }

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel id="client-visibility-client-label">
            Select Client
          </InputLabel>
          <Select
            labelId="client-visibility-client-label"
            id="client-visibility-client"
            value={selectedClientId === "" ? "" : selectedClientId}
            label="Select Client"
            onChange={handleClientChange}
            disabled={isLoadingClients}
          >
            {activeClients.map((client) => (
              <MenuItem key={client.id} value={client.id}>
                {client.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="Reload data" placement="right" enterDelay={1000}>
          <div>
            <IconButton
              size="small"
              aria-label="refresh"
              onClick={handleRefreshClick}
              disabled={selectedClientId === ""}
            >
              <RefreshIcon />
            </IconButton>
          </div>
        </Tooltip>
      </Stack>
      {overridesError ? (
        <Alert severity="error">Unable to load filter visibility.</Alert>
      ) : (
        <Paper
          elevation={2}
          sx={{
            height: "500px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <DataGrid
            rows={rows}
            getRowId={(row: ClientVisibilityRow) => row.filterTypeId}
            columns={getColumns({
              onToggleEnabled: handleToggleEnabled,
              onReset: handleReset,
            })}
            disableRowSelectionOnClick
            loading={
              isLoadingClients || isLoadingFilterTypes || isLoadingOverrides
            }
            sx={{ height: "100%" }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default ClientVisibility;
